"use client"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar } from "@fortawesome/free-solid-svg-icons"
import {
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Flex,
  TableContainer,
  Tooltip,
  useToast,
  Badge,
  HStack,
} from "@chakra-ui/react"
import { fetchProfileAsync, selectCompanyProfile } from "../employerDashAPIs/companyProfile/CompanyProfileSlice"
import {
  fetchCompanyReviewsAsync,
  selectCompanyReview,
  updateCompanyReviewsAsync,
} from "../employerDashboardSlice/employerCompanyReviews/CompanyReviewsSlice"
import noData from "./nodata1.png"

const FeedBack = () => {
  const dispatch = useDispatch()
  const reviews = useSelector(selectCompanyReview)
  const companyProfile = useSelector(selectCompanyProfile)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedReview, setSelectedReview] = useState(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [replyStatus, setReplyStatus] = useState("")
  const [replyText, setReplyText] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const toast = useToast()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        // Fetch both reviews and profile on initial mount
        await dispatch(fetchCompanyReviewsAsync())
        await dispatch(fetchProfileAsync())
        setLoading(false)
      } catch (err) {
        setError("Failed to load reviews. Please try again later.")
        setLoading(false)
      }
    }
    fetchData()
  }, [dispatch]) // Removed companyProfile?.uuid from dependencies to prevent double fetch on profile update

  const renderStars = (rating) => {
    return (
      <HStack spacing={1}>
        {[...Array(5)].map((_, i) => (
          <Tooltip key={i} label={`${rating} out of 5 stars`} placement="top">
            <span>
              <FontAwesomeIcon icon={faStar} className={i < rating ? "text-yellow-500" : "text-gray-300"} />
            </span>
          </Tooltip>
        ))}
      </HStack>
    )
  }

  const handleReplyClick = (review) => {
    setSelectedReview(review)
    setReplyStatus(review.status)
    setReplyText(review.review_reply || "")
    onOpen()
  }

  const handleSubmitReply = async () => {
    if (!selectedReview) return
    setSubmitting(true)
    try {
      await dispatch(
        updateCompanyReviewsAsync({
          uuid: selectedReview.uuid,
          data: {
            status: replyStatus,
            review_reply: replyText,
          },
        }),
      )
      // Re-fetch reviews after a successful update to reflect changes
      await dispatch(fetchCompanyReviewsAsync())
      // Success toast for reply submission
      toast({
        title: "Reply submitted successfully",
        description: `Your reply to "${selectedReview.review_title}" has been ${replyText ? "updated" : "added"}.`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      })
      onClose()
    } catch (error) {
      // Error toast for reply submission failure
      toast({
        title: "Failed to submit reply",
        description: "There was an error submitting your reply. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "green"
      case "pending":
        return "yellow"
      case "rejected":
        return "red"
      default:
        return "gray"
    }
  }

  const truncateText = (text, maxLength = 100) => {
    if (!text) return ""
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
  }

  if (loading) {
    return (
      <Box className=" min-h-screen">
        <div className="p-3 bg-mainBg">
          <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
            <Skeleton height="50px" mb={4} />
            <Skeleton height="200px" />
          </div>
        </div>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  const NoDataComponent = () => (
    <Box width="100%" height="calc(100vh - 250px)" display="flex" justifyContent="center" alignItems="center">
      <Flex direction="column" alignItems="center" justifyContent="center">
        <img
          src={noData || "/placeholder.svg"}
          className="object-contain h-[400px] w-[400px]"
          alt="No data available"
        />
        <Text fontSize="xl" fontWeight="semibold" color="gray.800" mb={2}>
          No FeedBack Found
        </Text>
        <Text color="gray.600" textAlign="center" maxWidth="md">
          We couldn't find any FeedBack at the moment. Please check back later.
        </Text>
      </Flex>
    </Box>
  )

  return (
    <Box className="" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
          <h1 className="sm:text-4xl text-2xl font-semibold text-left">Feedback Management</h1>
          <Text color="gray.600" mb={4}>
            You can reply to the feedback of employees.
          </Text>
          <div className="w-full bg-white rounded-xl">
            <TableContainer>
              {reviews && reviews.length > 0 ? (
                <Table variant="simple" className="">
                  <Thead>
                    <Tr>
                      <Th>
                        <Tooltip label="Employee rating for the company" placement="top">
                          Rating
                        </Tooltip>
                      </Th>
                      <Th>
                        <Tooltip label="Employee feedback details" placement="top">
                          Review
                        </Tooltip>
                      </Th>
                      <Th>
                        <Tooltip label="Your reply to the employee feedback" placement="top">
                          Reply
                        </Tooltip>
                      </Th>
                      <Th>
                        <Tooltip label="Current status of the feedback" placement="top">
                          Status
                        </Tooltip>
                      </Th>
                      <Th>
                        <Tooltip label="Actions you can perform on this feedback" placement="top">
                          Actions
                        </Tooltip>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {reviews.map((review) => (
                      <Tr key={review.id}>
                        <Td>{renderStars(review.review_rating)}</Td>
                        <Td>
                          <Tooltip label={review.review_title} placement="top">
                            <Text fontWeight="bold" isTruncated maxWidth="200px">
                              {review.review_title}
                            </Text>
                          </Tooltip>
                          <Text fontSize="sm" color="gray.500">
                            {review.job_title?.title} - {review.job_location?.title}
                          </Text>
                          <Tooltip label={review.review_description} placement="bottom">
                            <Text fontSize="sm" color="gray.600" mt={2}>
                              {truncateText(review.review_description)}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          {review.review_reply ? (
                            <Tooltip label={review.review_reply} placement="bottom">
                              <Text fontSize="sm" color="gray.600" mt={2}>
                                {truncateText(review.review_reply)}
                              </Text>
                            </Tooltip>
                          ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                              No reply yet
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Tooltip label={`This feedback is currently ${review.status}`} placement="top">
                            <Badge colorScheme={getStatusColor(review.status)} textTransform="capitalize" cursor="help">
                              {review.status}
                            </Badge>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Tooltip
                            label={review.review_reply ? "Update your existing reply" : "Add a reply to this feedback"}
                            placement="top"
                          >
                            <Button
                              onClick={() => handleReplyClick(review)}
                              colorScheme={review.review_reply ? "green" : "blue"}
                              size="sm"
                            >
                              {review.review_reply ? "Update Reply" : "Reply"}
                            </Button>
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <NoDataComponent />
              )}
            </TableContainer>
          </div>
          <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Reply to Review: {selectedReview?.review_title}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <FormControl mb={4}>
                  <FormLabel>
                    Status
                    <Tooltip label="Change the status of this feedback" placement="right">
                      <Text as="span" color="gray.400" ml={1}>
                        ℹ
                      </Text>
                    </Tooltip>
                  </FormLabel>
                  <Select value={replyStatus} onChange={(e) => setReplyStatus(e.target.value)}>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>
                    Reply
                    <Tooltip label="Write your response to the employee's feedback" placement="right">
                      <Text as="span" color="gray.400" ml={1}>
                        ℹ
                      </Text>
                    </Tooltip>
                  </FormLabel>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply to the employee's feedback..."
                    rows={4}
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Tooltip label="Submit your reply and status update" placement="top">
                  <Button
                    colorScheme="blue"
                    mr={3}
                    onClick={handleSubmitReply}
                    isLoading={submitting}
                    loadingText="Submitting..."
                  >
                    Submit Reply
                  </Button>
                </Tooltip>
                <Tooltip label="Cancel without saving changes" placement="top">
                  <Button variant="ghost" onClick={onClose} disabled={submitting}>
                    Cancel
                  </Button>
                </Tooltip>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </div>
      </div>
    </Box>
  )
}

export default FeedBack
